import buildUrl from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { statusLabelType, statusText } from 'modules/banking/lib/mappers'

import type { Payment } from './types'
import { isPaymentBase } from './types'

interface Props {
  locale: string
}

export const usePaymentsDataCollection = ({ locale }: Props) => {
  return {
    sortings: {
      date: {
        label: i18n.t('banking.payments.data_table.date'),
      },
      dueDate: {
        label: i18n.t('banking.payments.data_table.due_date'),
      },
      label: {
        label: i18n.t('banking.payments.data_table.name'),
      },
      amountCents: {
        label: i18n.t('banking.payments.data_table.amount'),
      },
      status: {
        label: i18n.t('banking.payments.data_table.status'),
      },
    },
    defaultSortings: {
      field: 'date' as const,
      order: 'desc' as const,
    },
    columns: [
      {
        label: i18n.t('banking.payments.data_table.date'),
        sorting: 'date' as const,
        width: 320,
        render: (item: Payment) => ({
          type: item.date ? ('date' as const) : ('text' as const),
          value: item.date
            ? dateBackendDateTimeToUI(item.date)
            : '_transferData' in item && item._transferData
            ? item._transferData.accountNumber ?? ''
            : '',
        }),
      },
      {
        label: i18n.t('banking.payments.data_table.due_date'),
        sorting: 'dueDate' as const,
        render: (item: Payment) => ({
          type: 'date' as const,
          value: item.dueDate ? dateBackendDateTimeToUI(item.dueDate) : '',
        }),
      },
      {
        label: i18n.t('banking.payments.data_table.name'),
        sorting: 'label' as const,
        render: (item: Payment) => ({
          type: 'text' as const,
          value: item.label || '',
        }),
      },
      {
        label: i18n.t('banking.payments.data_table.amount'),
        sorting: 'amountCents' as const,
        render: (item: Payment) => ({
          type: 'text' as const,
          value: MoneyWithCurrency({
            amount: item.amountCents || 0,
            currency: item.currency,
            locale,
          }),
        }),
      },
      {
        label: i18n.t('banking.payments.data_table.transfers'),
        render: (item: Payment) => {
          if (!isPaymentBase(item)) {
            return {
              type: 'text' as const,
              value: '0',
            }
          }
          const executions = item.paymentExecutions ?? []
          if (executions.length === 0) {
            return {
              type: 'text' as const,
              value: '0',
            }
          }
          const lastExecution = executions[executions.length - 1]
          const count = lastExecution?.transfersConnection?.totalCount || 0
          return {
            type: 'text' as const,
            value: count.toString(),
          }
        },
      },
      {
        label: i18n.t('banking.payments.data_table.created_by'),
        render: (item: Payment) => ({
          type: 'person' as const,
          value: item.requester
            ? {
                firstName: item.requester.fullName,
                lastName: '',
                src: item.requester.access?.avatar
                  ? buildUrl(item.requester.access.avatar)
                  : undefined,
              }
            : undefined,
        }),
      },
      {
        label: i18n.t('banking.payments.data_table.status'),
        sorting: 'status' as const,
        render: (item: Payment) => {
          // Type guard for status - ensure it's a valid enum value
          const status =
            item.status === 'init' ||
            item.status === 'pending_consent' ||
            item.status === 'rejected_consent' ||
            item.status === 'in_progress' ||
            item.status === 'rejected' ||
            item.status === 'done' ||
            item.status === 'paid' ||
            item.status === 'partially_paid'
              ? item.status
              : 'init'
          return {
            type: 'status' as const,
            value: {
              label: statusText(status),
              status: statusLabelType(status),
            },
          }
        },
      },
    ],
  }
}
